import React, { useMemo } from "react"
import ConditionalRender from "../../../ConditionalRender.js"
import { Icons } from "../Icons/index.js"

/**
 * Size → CSS class mapping.
 * Controls input height, padding, and font-size via SCSS.
 */
const InputBoxSizes = {
    Small: "small",
    Medium: "medium",
    Large: "large",
}

const InputBoxSizeFonts = {
    Small: "body-s-semibold",
    Medium: "body-s-semibold",
    Large: "body-m-semibold",
}

/**
 * HTML input type mappings.
 * Keys are PascalCase prop values, values are standard HTML type attributes.
 */
const InputBoxTypes = {
    Text: "text",
    Password: "password",
    Email: "email",
    Number: "number",
    Tel: "tel",
    Url: "url",
}

/**
 * InputBox — Versatile text input component.
 *
 * Two usage modes:
 * 1. Standalone: Pass value, handleOnChange, etc. — InputBox renders its own <input>.
 * 2. Wrapper: Pass a child <input> element — InputBox clones it and merges its classes,
 *    props, and disabled state. Child props take priority (nullish coalescing).
 *
 * Supports optional label, helper text, inline icon, error state, and disabled state.
 *
 * @param {string}  Size           - 'Small' | 'Medium' | 'Large'
 * @param {string}  Type           - 'Text' | 'Password' | 'Email' | 'Number' | 'Tel' | 'Url'
 * @param {string}  label          - Label text (shown above input when showLabel is true)
 * @param {string}  helperText     - Helper text below input (shown when showHelperText is true)
 * @param {string}  error          - Error message (also triggers error styling on the input)
 * @param {boolean} disabled       - Disabled state
 * @param {string}  IconName       - Icon to display inside the input (positioned left)
 * @param {func}    handleOnChange - Change handler
 */
export const InputBox = ({
    children,
    Size = "Medium",
    Type = "Text",
    label = "",
    showLabel = false,
    labelClass = "input-box-label body-s-semibold",
    showHelperText = false,
    helperText = "",
    helperTextClass = "helper-text support-m",
    helperTextStyle = {},
    inputStyle = {},
    value,
    placeholder = "",
    customClass = "",
    customStyle = {},
    showIcon = false,
    IconName = null,
    placeholderClass = "",
    IconClass = "icon",
    IconStyle = {},
    handleOnChange,
    handleOnKeyDown,
    onClick,
    name,
    error = "",
    disabled = false,
    fontClass = null,
    innerInputStyle = {},
    readOnly = false,
    containerClass = "",
}) => {
    const inputSize = InputBoxSizes[Size]
    const inputType = InputBoxTypes[Type]

    // Memoize class string — avoids recomputing on every render when props haven't changed
    const computed_classes = useMemo(() =>
        [
            "input-box",
            `input-box-${inputSize}`,
            fontClass ?? InputBoxSizeFonts[Size],
            error && "input-box-error",
            disabled && "input-box-disabled",
            showIcon && !!IconName && "input-box--has-leading-icon",
            customClass,
            placeholderClass,
        ].filter(Boolean).join(" "),
        [inputSize, Size, fontClass, error, disabled, showIcon, IconName, customClass, placeholderClass]
    );

    /**
     * Renders the actual <input> element.
     * If children are provided and are a valid React element, clones them with merged props.
     * Otherwise renders a default <input>.
     */
    const renderInput = () => {
        if (!children) {
            return (
                <input
                    className={computed_classes}
                    disabled={disabled}
                    type={inputType}
                    value={value}
                    name={name}
                    placeholder={placeholder}
                    onChange={handleOnChange}
                    onKeyDown={handleOnKeyDown}
                    onClick={onClick}
                    style={innerInputStyle}
                    readOnly={readOnly}
                />
            )
        }

        // Clone child input: merge our classes and props, but let child props win via ??
        if (React.isValidElement(children)) {
            return React.cloneElement(children, {
                className: [children.props.className, computed_classes].filter(Boolean).join(" "),
                disabled: children.props.disabled ?? disabled,
                type: children.props.type ?? inputType,
                value: children.props.value ?? value,
                placeholder: children.props.placeholder ?? placeholder,
                onChange: children.props.onChange ?? handleOnChange,
                onKeyDown: children.props.onKeyDown ?? handleOnKeyDown,
                onClick: children.props.onClick ?? onClick,
                style: children.props.style ?? innerInputStyle,
                readOnly: children.props.readOnly ?? readOnly,
            })
        }

        return children
    }

    return (
        <div className={containerClass} style={{ ...customStyle }}>
            <ConditionalRender if={showLabel && label !== ""}>
                <div className={labelClass}>{label}</div>
            </ConditionalRender>
            <div style={{ position: "relative", ...inputStyle }}>
                {renderInput()}
                <ConditionalRender if={showIcon && !!IconName}>
                    <span className={`input-box__leading-icon ${IconClass}`} style={IconStyle}>
                        <Icons name={IconName} size={16} />
                    </span>
                </ConditionalRender>
            </div>
            <ConditionalRender if={showHelperText && helperText !== ""}>
                <div className={helperTextClass} style={helperTextStyle}>
                    {helperText}
                </div>
            </ConditionalRender>
        </div>
    )
}